import React from "react";
import { formatNOK } from "@/lib/format";
import { useSalesUnits } from "@/hooks/use-sales-units";
import type { DBCustomer } from "@/lib/db-types";
import type { UserLookup } from "@/hooks/use-users";

interface SelectedSalesUnitInput {
  salesUnitId: string;
  ownershipPct: number;
}

interface StepConfirmationProps {
  customers: DBCustomer[] | undefined;
  customerId: string;
  projectName: string;
  projectCode: string;
  projectStatus: string;
  totalAmount: number;
  deadline: string;
  isInternal: boolean;
  users: UserLookup[] | undefined;
  productionLeadId: string;
  customerContactPerson: string | undefined;
  customerContactEmail: string | undefined;
  selectedSalesUnits: SelectedSalesUnitInput[];
  startAmount: number;
  paymentDueDays: number;
  invoiceEmail: string;
  customerReferencePerson: string;
  customerReferenceNumber: string;
  budgetLinesCount: number;
  budgetTotal: number;
  totalHours: number;
}

export function StepConfirmation({
  customers,
  customerId,
  projectName,
  projectCode,
  projectStatus,
  totalAmount,
  deadline,
  isInternal,
  users,
  productionLeadId,
  customerContactPerson,
  customerContactEmail,
  selectedSalesUnits,
  startAmount,
  paymentDueDays,
  invoiceEmail,
  customerReferencePerson,
  customerReferenceNumber,
  budgetLinesCount,
  budgetTotal,
  totalHours,
}: StepConfirmationProps) {
  const { data: salesUnits } = useSalesUnits(new Date().getFullYear());
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium mb-3">Grunninfo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          {!isInternal && (
            <>
              <span className="text-muted-foreground">Kunde:</span>
              <span>{customers?.find(c => c.id === customerId)?.name ?? "Ikke valgt"}</span>
            </>
          )}
          <span className="text-muted-foreground">Prosjektnavn:</span>
          <span>{projectName || "—"}</span>
          <span className="text-muted-foreground">Prosjektkode:</span>
          <span>{projectCode || "—"}</span>
          <span className="text-muted-foreground">Prosjektstatus:</span>
          <span>{projectStatus === "active" ? "Aktivt" : "Kommende"}</span>
          {!isInternal && (
            <>
              <span className="text-muted-foreground">Totalbeløp:</span>
              <span>{formatNOK(totalAmount)}</span>
              <span className="text-muted-foreground">Deadline:</span>
              <span>{deadline || "—"}</span>
            </>
          )}
          <span className="text-muted-foreground">Intern:</span>
          <span>{isInternal ? "Ja" : "Nei"}</span>
          <span className="text-muted-foreground">Prosjektleder:</span>
          <span>{users?.find(u => u.id === productionLeadId)?.full_name || "—"}</span>
          {!isInternal && (
            <>
              <span className="text-muted-foreground">Kontaktperson (kunde):</span>
              <span>{customerContactPerson || "—"}</span>
              <span className="text-muted-foreground">E-post (kunde):</span>
              <span>{customerContactEmail || "—"}</span>
            </>
          )}
        </div>
      </div>

      {!isInternal && (
        <div>
          <h3 className="font-medium mb-3">Eiere og fakturering</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <span className="text-muted-foreground">Selgere:</span>
            <span>
              {selectedSalesUnits
                .map(o => {
                  const u = salesUnits?.find(s => s.id === o.salesUnitId);
                  return `${u?.name ?? "?"} (${o.ownershipPct}%)`;
                })
                .join(", ") || "—"}
            </span>
            <span className="text-muted-foreground">Oppstartfaktura:</span>
            <span>{formatNOK(startAmount)}</span>
            <span className="text-muted-foreground">Forfallsdager:</span>
            <span>{paymentDueDays} dager</span>
            <span className="text-muted-foreground">Fakturamail:</span>
            <span>{invoiceEmail || "—"}</span>
            <span className="text-muted-foreground">Deres ref.:</span>
            <span>{customerReferencePerson || "—"}</span>
            <span className="text-muted-foreground">Deres ref. kode:</span>
            <span>{customerReferenceNumber || "—"}</span>
          </div>
        </div>
      )}

      {!isInternal && (
        <div>
          <h3 className="font-medium mb-3">Budsjett</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <span className="text-muted-foreground">Budsjettposter:</span>
            <span>{budgetLinesCount} linjer, {formatNOK(budgetTotal)}</span>
            <span className="text-muted-foreground">Timer totalt:</span>
            <span>{totalHours}t</span>
            {totalAmount > 0 && (
              <>
                <span className="text-muted-foreground">Tilbudssum:</span>
                <span className="font-medium">{formatNOK(totalAmount)}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
